import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { ArrowLeft, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QUESTIONS = [
  { key: 'q1', text: 'Sei claramente o que é esperado de mim no trabalho.' },
  { key: 'q2', text: 'Tenho o que preciso para fazer bem o meu trabalho.' },
  { key: 'q3', text: 'Sinto que meu trabalho é reconhecido e valorizado.' },
  { key: 'q4', text: 'Tenho oportunidades de aprender e crescer.' },
  { key: 'q5', text: 'Meu líder ou alguém na empresa se importa com meu desenvolvimento.' },
  { key: 'q6', text: 'Minhas opiniões são ouvidas no ambiente de trabalho.' },
  { key: 'q7', text: 'Sinto que faço parte de uma equipe que confia uns nos outros.' },
  { key: 'q8', text: 'O ambiente de trabalho me permite ser quem eu sou.' },
];

const LABELS = ['Discordo\ntotalmente', 'Discordo', 'Neutro', 'Concordo', 'Concordo\ntotalmente'];

/**
 * WellbeingSurvey — monthly 8-question relational wellbeing survey.
 *
 * Props:
 *   onClose — called when dismissed or submitted
 */
const WellbeingSurvey = ({ onClose }) => {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const total = QUESTIONS.length;
  const answered = Object.keys(answers).length;
  const currentQ = QUESTIONS[currentIdx];
  const isFirst = currentIdx === 0;
  const isLast = currentIdx === total - 1;
  const canAdvance = answers[currentQ.key] !== undefined;

  const handleAnswer = (value) => {
    setAnswers((prev) => ({ ...prev, [currentQ.key]: value }));
  };

  const handleSubmit = async () => {
    if (!canAdvance) return;
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/surveys/wellbeing`, answers, { withCredentials: true });
      setSubmitted(true);
      setTimeout(onClose, 2500);
    } catch {
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl p-6 relative">

        {submitted ? (
          <div className="text-center py-10">
            <div className="text-5xl mb-4">🌱</div>
            <p className="text-xl font-bold text-gray-900">Obrigada!</p>
            <p className="text-sm text-gray-500 mt-2 leading-relaxed">
              Seus dados ajudam a Paula a entender a saúde da sua equipe e direcionar o treinamento.
            </p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="mb-5">
              <h3 className="text-lg font-bold text-gray-900">Pesquisa de Bem-estar Relacional</h3>
              <p className="text-sm text-gray-500 mt-1">
                Como você está se sentindo no trabalho este mês? Responda com honestidade — as respostas são anônimas para sua gestora.
              </p>
            </div>

            {/* Progress bar */}
            <div className="mb-6">
              <div className="flex justify-between text-xs text-gray-400 mb-1">
                <span>{answered} de {total} respondidas</span>
                <span>{currentIdx + 1} de {total}</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2">
                <div
                  className="bg-purple-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(currentIdx / total) * 100}%` }}
                />
              </div>
            </div>

            {/* Current question */}
            <div className="mb-6">
              <p className="text-sm font-medium text-gray-800 mb-4">
                <span className="text-purple-500 font-bold mr-1">{currentIdx + 1}.</span>
                {currentQ.text}
              </p>
              <div className="flex gap-2 justify-between">
                {[1, 2, 3, 4, 5].map((val) => (
                  <button
                    key={val}
                    onClick={() => handleAnswer(val)}
                    className={`flex-1 flex flex-col items-center gap-1 py-2 rounded-xl border-2 text-xs transition-all ${
                      answers[currentQ.key] === val
                        ? 'bg-purple-600 border-purple-600 text-white font-bold scale-105'
                        : 'border-gray-200 text-gray-500 hover:border-purple-300 hover:bg-purple-50'
                    }`}
                  >
                    <span className="text-base font-semibold">{val}</span>
                    <span className="leading-tight text-center whitespace-pre-line hidden sm:block">
                      {LABELS[val - 1]}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Navigation */}
            <div className="flex gap-3">
              {!isFirst && (
                <Button
                  variant="outline"
                  className="flex items-center gap-1"
                  onClick={() => setCurrentIdx(i => i - 1)}
                >
                  <ArrowLeft className="w-4 h-4" />
                  Anterior
                </Button>
              )}

              {isLast ? (
                <Button
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                  disabled={!canAdvance || isSubmitting}
                  onClick={handleSubmit}
                >
                  {isSubmitting ? 'Enviando…' : 'Enviar respostas'}
                </Button>
              ) : (
                <Button
                  className="flex-1 bg-purple-600 hover:bg-purple-700 flex items-center justify-center gap-1"
                  disabled={!canAdvance}
                  onClick={() => setCurrentIdx(i => i + 1)}
                >
                  Próxima
                  <ArrowRight className="w-4 h-4" />
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default WellbeingSurvey;
